import { useCallback, useState } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useStages } from '../../../services/entities/hooks/useStages';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { Scan } from '../../../services/entities/impl/blfApi/schemas/scan';
import { useAdaptivePointColorConfig } from '../../commonUI/maps/hooks/useAdaptivePointColorConfig/useAdaptivePointColorConfig';
import { usePointHeatmap } from '../../commonUI/maps/hooks/usePointHeatmap';
import { SINGLE_POINT_LAYER_ID } from '../../commonUI/maps/layers/constants';
import { PointHeatmap } from '../../commonUI/maps/PointHeatmap';
import { PointFeatureProperties } from '../../commonUI/maps/types';
import { BLOCK_POINTS_MAP_COLOR_CONFIG } from './constants';
import { BlockDetailsControl } from './controls/BlockDetailsControl';
import {
  BlockPointDetailsControl,
  BlockPointDetailsControlProps,
} from './controls/BlockPointDetailsControl';
import { ScanControl } from './controls/ScanControl';
import { BlockPointsMapControlFilters } from './controls/types';
import { DebugInfoLauncherControl } from './debugInfo/DebugInfoLauncherControl';
import { BlockPointData, useGetBlockPoints } from './hooks/useGetBlockPoints';

export const BlockPointsMap = ({
  block,
  scan,
  loading,
}: {
  block: Block;
  scan: Scan;
  loading: boolean;
}) => {
  const { points, loading: pointsLoading } = useGetBlockPoints(
    block.id,
    scan.id,
    loading
  );

  // Derive an adaptive color config based on current point values to cap outliers
  const adaptiveColorConfig = useAdaptivePointColorConfig(
    points ?? [],
    BLOCK_POINTS_MAP_COLOR_CONFIG
  );
  const blocks = useDeepCompareMemo(() => [block], [block]);
  const { pointsFeatureCollection, blocksFeatureCollection } =
    usePointHeatmap<BlockPointData>(points ?? [], adaptiveColorConfig, blocks);

  const { stages: allStages } = useStages(block.varietyID);

  /**
   * PointDetailsViewer action handlers
   */
  const [selectedPoint, setSelectedPoint] =
    useState<PointFeatureProperties<BlockPointData>>();
  const handleDataDisplayClose = useCallback(() => {
    setSelectedPoint(undefined);
  }, []);

  const handleNextPoint = useCallback(() => {
    const pointFeatures = pointsFeatureCollection.features;
    setSelectedPoint((prevSelectedPoint) => {
      if (prevSelectedPoint) {
        const indexOfNewPoint =
          prevSelectedPoint?.index === pointFeatures.length - 1
            ? 0
            : prevSelectedPoint.index + 1;
        return pointFeatures[indexOfNewPoint].properties;
      }
    });
  }, [pointsFeatureCollection.features]);

  const handlePreviousPoint = useCallback(() => {
    const pointFeatures = pointsFeatureCollection.features;
    setSelectedPoint((prevSelectedPoint) => {
      if (prevSelectedPoint) {
        const indexOfNewPoint =
          prevSelectedPoint?.index === 0
            ? pointFeatures.length - 1
            : prevSelectedPoint.index - 1;
        return pointFeatures[indexOfNewPoint].properties;
      }
    });
  }, [pointsFeatureCollection.features]);

  /**
   * Map Layer Event Handlers
   */
  const handleMapLayerClick = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      if (event.features && event.features.length > 0) {
        const { layer, properties } = event.features[0];
        switch (layer.id) {
          case SINGLE_POINT_LAYER_ID:
            setSelectedPoint(
              properties as PointFeatureProperties<BlockPointData> | undefined
            );
            break;
          default:
            break;
        }
      }
    },
    []
  );

  const [filters, setFilters] = useState<BlockPointsMapControlFilters>({
    stages: undefined,
    sizes: undefined,
  });
  const handleControlAction = useCallback<
    BlockPointDetailsControlProps['onControlAction']
  >((actionType, payload) => {
    switch (actionType) {
      case 'FILTER':
        setFilters(payload);
    }
  }, []);

  if (loading) return null;

  return (
    <PointHeatmap<BlockPointData>
      pointsFeatureCollection={pointsFeatureCollection}
      blocksFeatureCollection={blocksFeatureCollection}
      pointColorConfig={adaptiveColorConfig}
      selectedPointId={selectedPoint?.id}
      onMapClick={handleMapLayerClick}
    >
      <ScanControl block={block} scan={scan} />
      {/* Map Controls */}
      <BlockDetailsControl
        block={block}
        scanId={scan.id}
        stages={allStages}
        loading={loading}
        filters={filters}
        onControlAction={handleControlAction}
      />
      <BlockPointDetailsControl
        block={block}
        point={selectedPoint}
        stages={allStages}
        filters={filters}
        onControlAction={handleControlAction}
        onNextPoint={handleNextPoint}
        onPreviousPoint={handlePreviousPoint}
        open={!!selectedPoint && !pointsLoading}
        onClose={handleDataDisplayClose}
        positions={{
          left: 8,
          top: 109,
        }}
      />
      <DebugInfoLauncherControl
        blockId={block.id}
        scanId={scan.id}
        points={points}
        selectedPoint={selectedPoint}
      />
    </PointHeatmap>
  );
};
